import sanitizeHtml from 'sanitize-html';
import { excludedSelectors } from '../components/core/withCopyPasteProtection';

export const getSanitizedHtml = (html) => {
  return sanitizeHtml(html, {
    allowedTags: [
      'address',
      'article',
      'aside',
      'footer',
      'header',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'hgroup',
      'main',
      'nav',
      'section',
      'blockquote',
      'dd',
      'div',
      'dl',
      'dt',
      'figcaption',
      'figure',
      'hr',
      'li',
      'main',
      'ol',
      'p',
      'pre',
      'ul',
      'a',
      'abbr',
      'b',
      'bdi',
      'bdo',
      'br',
      'cite',
      'code',
      'data',
      'dfn',
      'em',
      'i',
      'kbd',
      'mark',
      'q',
      'rb',
      'rp',
      'rt',
      'rtc',
      'ruby',
      's',
      'samp',
      'small',
      'span',
      'strong',
      'sub',
      'sup',
      'time',
      'u',
      'var',
      'wbr',
      'caption',
      'col',
      'colgroup',
      'table',
      'tbody',
      'td',
      'tfoot',
      'th',
      'thead',
      'tr',
      'iframe'
    ],
    disallowedTagsMode: 'discard',
    allowedAttributes: {
      a: ['href', 'name', 'target'],
      iframe: ['src', 'width', 'height', 'frameborder', 'allow', 'allowfullscreen', 'title'],
      img: ['src', 'srcset', 'alt', 'title', 'width', 'height', 'loading']
    },
    allowedIframeHostnames: ['www.youtube.com', 'youtube.com', 'www.youtube-nocookie.com']
  });
};

export const isValidEmail = (email) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};

export const isValidStartWithDetailsURL = (url) => {
  const regex = new RegExp(
    /^https:\/\/[-a-zA-Z0-9]+\.getclasswise\.com\/start-details\/[0-9a-fA-F]{24}$/
  );
  return regex.test(url);
};

export const isValidClasswiseAssignmentURL = (url) => {
  const regex = new RegExp(/^https:\/\/[-a-zA-Z0-9]+\.getclasswise\.com\/start\/[0-9a-fA-F]{24}$/);
  return regex.test(url);
};

export const sanitizeMessage = (value) => {
  if (value.indexOf('\\') === -1) {
    return value;
  }
  return value
    .trim()
    .replaceAll('|\\(', '$')
    .replaceAll('\\)', '$')
    .replaceAll('\\[', '$$')
    .replaceAll('\\]', '$$')
    .replaceAll('\\(', '$$')
    .replaceAll('\\)', '$$');
};

export const removeMcqOptions = (value) => {
  return value.replaceAll(/<mcq-option>[\s\S]*?<\/mcq-option>/g, '');
};

export const getCurrencySymbol = (currencyCode) => {
  return (0)
    .toLocaleString('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })
    .replace(/\d/g, '')
    .trim();
};

export function removeUrlParam(relativeUrl, paramName) {
  // Split the URL into the path and the query string
  const [path, queryString] = relativeUrl.split('?');

  if (!queryString) {
    return relativeUrl; // No query parameters to remove
  }

  // Convert the query string into an array of key-value pairs
  const params = new URLSearchParams(queryString);

  // Delete the specific parameter if it exists
  params.delete(paramName);

  // Reconstruct the URL
  const newQueryString = params.toString();
  const newUrl = newQueryString ? `${path}?${newQueryString}` : path;

  return newUrl;
}

export function matchTargetElement(target) {
  if (!target) return false;

  const element =
    target.nodeType === Node.ELEMENT_NODE ? target : target.parentElement;

  if (!element || typeof element.closest !== 'function') {
    return false;
  }

  const allowedParent = excludedSelectors.some((selector) => element.closest(selector));

  // Subjective / code answer editors still allow copy-paste inside the editor
  return (
    !!allowedParent ||
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    (element instanceof HTMLElement && element.isContentEditable)
  );
}

const decodeBasicHtmlEntities = (value = '') =>
  String(value)
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");

const stripHtmlTags = (value = '') =>
  decodeBasicHtmlEntities(value)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Normalize AI evaluation reasons for display.
 * Handles stored/legacy messages that include raw HTML snippets or "[object Object]".
 */
export const formatAiReasonForDisplay = (reason) => {
  if (reason == null || reason === '') return '';

  if (typeof reason === 'object') {
    if (typeof reason.html === 'string') return formatAiReasonForDisplay(reason.html);
    if (typeof reason.text === 'string') return formatAiReasonForDisplay(reason.text);
    if (typeof reason.message === 'string') return formatAiReasonForDisplay(reason.message);
    return 'Evaluation details are unavailable for this response.';
  }

  let text = String(reason).trim();
  if (!text) return '';

  // Replace quoted HTML snippets: '<p>test</p>' → "test"
  text = text.replace(/(['"])\s*(<[^>]*>[\s\S]*?<\/[^>]*>|<[^>]*\/?>)\s*\1/g, (_match, _q, html) => {
    const plain = stripHtmlTags(html);
    return plain ? `"${plain}"` : 'the question';
  });

  // Replace unquoted HTML fragments that still appear in the sentence
  text = text.replace(/<[^>]+>/g, ' ');

  // Clean up object-stringification artifacts from bad grading payloads
  text = text
    .replace(/\bthe HTML\b/gi, '')
    .replace(/\bHTML\b/gi, '')
    .replace(/\[\s*object\s+Object\s*\]/gi, 'the submitted response')
    .replace(/['"]\s*the submitted response\s*['"]/gi, 'the submitted response')
    .replace(/\bthe response the submitted response\b/gi, 'the submitted response')
    .replace(/\bresponse the submitted response\b/gi, 'submitted response')
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;:!?])/g, '$1')
    .trim();

  // Friendlier rewrite for the known broken evaluation pattern
  const brokenEvalPattern =
    /submitted response.*not a valid evaluation|not a valid evaluation.*submitted response|provides no assessment/i;
  if (brokenEvalPattern.test(text)) {
    const quoted = text.match(/"([^"]+)"/);
    const questionHint = quoted?.[1] ? ` for “${quoted[1]}”` : '';
    return `The evaluation could not assess this response${questionHint}. Please review the answer manually or re-evaluate.`;
  }

  return text;
};
